/**
 * Zustand store for pinned AGGREGATIONS / PORTFOLIOS (SHA-43 portfolio CRUD).
 *
 * Mirrors pinnedStrategiesStore but operates on aggregation (portfolio) ids.
 * Pinned portfolios float to the top of the PortfolioSidebar list.
 *
 * Persisted to localStorage so pin state survives page reloads.
 * RULE 13: no compute; persistence is client-side localStorage only.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ── Constants ─────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'sl-pinned-aggregations';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PinnedAggregationsStore {
  /** Aggregation ids currently pinned, most-recently-pinned first. */
  pinned: string[];
  /** Pin an aggregation id (idempotent — moves it to front if already pinned). */
  pin: (id: string) => void;
  /** Remove an aggregation id from the pinned list (no-op if not present). */
  unpin: (id: string) => void;
  /** Toggle pin state for an aggregation id. */
  toggle: (id: string) => void;
  /** Whether an aggregation id is currently pinned. */
  isPinned: (id: string) => boolean;
  /**
   * Clear all pinned ids (and the persisted localStorage entry — the persist
   * middleware writes through synchronously on `set`).
   * MUST run on logout/login (SHA-71): pinned ids point to a specific
   * account's aggregations but are keyed under a fixed storage key
   * (`sl-pinned-aggregations`), not the auth token, so they otherwise
   * survive across accounts on the same browser.
   */
  clearAll: () => void;
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const usePinnedAggregationsStore = create<PinnedAggregationsStore>()(
  persist(
    (set, get) => ({
      pinned: [],

      pin: (id) =>
        set((state) => {
          const without = state.pinned.filter((p) => p !== id);
          return { pinned: [id, ...without] };
        }),

      unpin: (id) =>
        set((state) => ({
          pinned: state.pinned.filter((p) => p !== id),
        })),

      toggle: (id) => {
        if (get().isPinned(id)) {
          get().unpin(id);
        } else {
          get().pin(id);
        }
      },

      isPinned: (id) => get().pinned.includes(id),

      clearAll: () => set({ pinned: [] }),
    }),
    {
      name: STORAGE_KEY,
      // Only persist the `pinned` array — actions are not serializable
      partialize: (state) => ({ pinned: state.pinned }),
    }
  )
);
